import { Button, ButtonProps } from '@atlassiansox/wpl-utils';
import { MouseEvent, useCallback, useRef, useState } from 'react';
import { useTotal } from '../../../../selectors';
import { useGlobalStore } from '../../../../store';
import { PriceDisplay } from './price-display';
import { PriceFontSizeWidthCheck } from './price-font-size-width-check';
import {
  BasePriceContainer,
  ContactSalesContainer,
  EstimatedCostContainer,
  PriceTotalContainer,
  TaxNotIncludedContainer
} from './price-total.styles';
import {
  WithAnalyticsEventsProps,
  withAnalyticsEvents
} from '@atlaskit/analytics-next';
import { ANALYTICS_EVENTS } from '../../../../constants';
import { useModuleProps } from '../../../../module-props';
import DownloadPdf from '../../../download-pdf/download-pdf';

export interface PriceTotalProps {
  isCurrency?: boolean;
  iscontactSalesButton?: boolean;
  readonly contactSalesUrl: string;
}

const ContactSalesButtonBase = ({
  createAnalyticsEvent,
  onClick,
  ...rest
}: WithAnalyticsEventsProps & ButtonProps) => {
  const handleClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      const analyticsEvent = createAnalyticsEvent?.({
        action: 'click',
        component: 'button',
        eventName: ANALYTICS_EVENTS.contactSales
      });

      analyticsEvent?.fire();

      if (onClick) {
        onClick(e);
      }
    },
    [onClick, createAnalyticsEvent]
  );

  return <Button {...rest} onClick={handleClick} />;
};

const ContactSalesButton = withAnalyticsEvents()(ContactSalesButtonBase);

const PriceTotal = ({
  contactSalesUrl,
  iscontactSalesButton = true
}: PriceTotalProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState('100%');
  const [formattedTotalPrice] = useTotal();
  const [{ selectedBillingCycle }] = useGlobalStore();
  const {
    estimatedMonthlyCostLabel,
    estimatedAnnulaCostLabel,
    contactSalesLabel,
    taxNotIncludedLabel
  } = useModuleProps();

  return (
    <div css={PriceTotalContainer} ref={containerRef}>
      <PriceFontSizeWidthCheck
        cardWidth={containerRef.current?.clientWidth || 236}
        onFontSizeHandler={setFontSize}
        formattedPrice={formattedTotalPrice}
      />
      <div css={BasePriceContainer}>
        <PriceDisplay
          fontSize={fontSize}
          formattedPrice={formattedTotalPrice}
        />
      </div>
      <div css={EstimatedCostContainer} id="estimatedCost">
        {selectedBillingCycle === 'MONTHLY'
          ? estimatedMonthlyCostLabel
          : estimatedAnnulaCostLabel}
      </div>
      <div css={TaxNotIncludedContainer}>{taxNotIncludedLabel}</div>
      <div css={ContactSalesContainer}>
        <ContactSalesButton
          type={iscontactSalesButton ? 'primary' : 'secondary'}
          text={contactSalesLabel}
          className={'contact-sales-button'}
          url={contactSalesUrl}
          onClick={(e) => {
            e.preventDefault();
            window.location.href = contactSalesUrl;
          }}
        />
      </div>
      <DownloadPdf />
    </div>
  );
};

export { PriceTotal };
